import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Search, User } from 'lucide-react';
import { useSuperadminUsers, setUserRole, removeUserFromTenant, type SuperadminUser } from '@/hooks/useSuperadmin';
import { useToast } from '@/hooks/use-toast';
import { format, formatDistanceToNow } from 'date-fns';

const ROLE_OPTIONS = [
  'user', 'project_manager', 'senior_project_manager', 'program_manager',
  'executive', 'pmo', 'admin',
] as const;

const SuperadminUsers = () => {
  const { users, loading, refetch } = useSuperadminUsers();
  const [search, setSearch] = useState('');
  const { toast } = useToast();
  const [confirmRemove, setConfirmRemove] = useState<SuperadminUser | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const handleRoleChange = async (user: SuperadminUser, role: string) => {
    if (!user.tenant_id) return;
    setBusyId(user.user_id);
    try {
      await setUserRole(user.user_id, user.tenant_id, role);
      toast({ title: `${user.email} is now ${role.replace(/_/g, ' ')}` });
      refetch();
    } catch (e) {
      toast({ title: 'Failed to update role', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemove?.tenant_id) return;
    setBusyId(confirmRemove.user_id);
    try {
      await removeUserFromTenant(confirmRemove.user_id, confirmRemove.tenant_id);
      toast({ title: `${confirmRemove.email} removed from ${confirmRemove.tenant_name}` });
      refetch();
    } catch (e) {
      toast({ title: 'Failed to remove user', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
      setConfirmRemove(null);
    }
  };

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.tenant_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">All Users</h1>
        <p className="text-muted-foreground text-sm">{users.length} users across all organisations</p>
      </div>

      <Card>
        <CardHeader>
          <div className="relative max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or org..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-9"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">User</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Organisation</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Role</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Last Active</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Joined</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((u) => (
                <tr key={u.user_id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{u.email?.split('@')[0]}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {u.tenant_name ? (
                      <div>
                        <div className="font-medium">{u.tenant_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">/{u.tenant_slug}</div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {u.tenant_id ? (
                      <Select value={u.role} onValueChange={(v) => handleRoleChange(u, v)} disabled={busyId === u.user_id}>
                        <SelectTrigger className="w-44 h-8 text-xs capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((r) => (
                            <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-muted-foreground text-xs">
                    {u.last_active
                      ? formatDistanceToNow(new Date(u.last_active), { addSuffix: true })
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-muted-foreground text-xs">
                    {format(new Date(u.created_at), 'MMM d, yyyy')}
                  </td>
                  <td className="px-6 py-4">
                    {u.tenant_id && (
                      <Button size="sm" variant="outline" disabled={busyId === u.user_id} onClick={() => setConfirmRemove(u)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">No users found</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmRemove?.email} from {confirmRemove?.tenant_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This revokes their access to this organisation. Their account itself is not deleted — they can be added to a different organisation later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuperadminUsers;
